// src/lib/privacy/dataAggregation.ts

import { db } from '../../firebase/config';
import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  query, 
  where, 
  orderBy 
} from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';
import { ExportedUserData } from '../../types/privacyRequest';
import { loadAllEntityFormationDrafts } from '../intake/entityFormationDraftApi';
import { loadAllEntityFormationSubmissions } from '../intake/entityFormationSubmitApi';

/**
 * Aggregates all user data across Firestore collections for privacy requests
 */
export async function aggregateUserData(userId: string, email: string, requestId: string): Promise<ExportedUserData> {
  const exportedAt = new Date().toISOString();
  
  // Get profile data from users collection
  let profile: any = null;
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDocSnap = await getDoc(userDocRef);
    if (userDocSnap.exists()) {
      profile = {
        id: userDocSnap.id,
        ...userDocSnap.data()
      };
    }
  } catch (error) {
    console.error('Error fetching user profile:', error);
  }
  
  // Get client/business data
  let businessData: any = null;
  try {
    const clientsRef = collection(db, 'clients');
    const clientQuery = query(clientsRef, where('userId', '==', userId));
    const clientSnapshot = await getDocs(clientQuery);
    if (!clientSnapshot.empty) {
      const clientDoc = clientSnapshot.docs[0];
      businessData = {
        id: clientDoc.id,
        ...clientDoc.data()
      };
    }
  } catch (error) {
    console.error('Error fetching client data:', error);
  }
  
  // Get all tasks assigned to this user
  const tasks: any[] = [];
  try {
    const tasksRef = collection(db, 'tasks');
    const tasksQuery = query(
      tasksRef, 
      where('clientId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const tasksSnapshot = await getDocs(tasksQuery);
    tasksSnapshot.forEach((taskDoc) => {
      tasks.push({
        id: taskDoc.id,
        ...taskDoc.data()
      });
    });
  } catch (error) {
    console.error('Error fetching tasks:', error);
  }
  
  // Get all documents
  const documents: any[] = [];
  try {
    const documentsRef = collection(db, 'documents');
    const documentsQuery = query(
      documentsRef,
      where('ownerId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const documentsSnapshot = await getDocs(documentsQuery);
    documentsSnapshot.forEach((docDoc) => {
      documents.push({
        id: docDoc.id,
        ...docDoc.data()
      });
    });
  } catch (error) {
    console.error('Error fetching documents:', error);
  }
  
  // Get entity formation drafts
  let entityDrafts: any[] = [];
  try {
    const drafts = await loadAllEntityFormationDrafts(userId);
    entityDrafts = drafts.map(d => ({
      draftId: d.draftId,
      data: d.data,
      createdAt: d.createdAt?.toDate?.()?.toISOString(),
      updatedAt: d.updatedAt?.toDate?.()?.toISOString()
    }));
  } catch (error) {
    console.error('Error fetching entity formation drafts:', error);
  }
  
  // Get entity formation submissions
  let entitySubmissions: any[] = [];
  try {
    const submissions = await loadAllEntityFormationSubmissions(userId);
    entitySubmissions = submissions.map(s => ({
      submissionId: s.submissionId,
      data: s.data,
      createdAt: s.createdAt?.toDate?.()?.toISOString(),
      updatedAt: s.updatedAt?.toDate?.()?.toISOString(),
      submittedAt: s.submittedAt?.toDate?.()?.toISOString()
    }));
  } catch (error) {
    console.error('Error fetching entity formation submissions:', error);
  }
  
  // Get subscriptions
  const subscriptions: any[] = [];
  try {
    const subscriptionsRef = collection(db, 'subscriptions');
    const subscriptionsQuery = query(
      subscriptionsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
    subscriptionsSnapshot.forEach((subDoc) => {
      subscriptions.push({
        id: subDoc.id,
        ...subDoc.data()
      });
    });
  } catch (error) {
    console.error('Error fetching subscriptions:', error);
  }
  
  // Note: Audit logs are typically admin-only, but we can include user-related ones
  const auditLogs: any[] = [];
  try {
    const auditLogsRef = collection(db, 'auditLogs');
    const auditLogsQuery = query(
      auditLogsRef,
      where('userId', '==', userId),
      orderBy('timestamp', 'desc')
    );
    const auditLogsSnapshot = await getDocs(auditLogsQuery);
    auditLogsSnapshot.forEach((logDoc) => {
      auditLogs.push({
        id: logDoc.id,
        ...logDoc.data()
      });
    });
  } catch (error) {
    console.error('Error fetching audit logs:', error);
  }
  
  // Convert Firestore timestamps to ISO strings for JSON export
  const convertTimestamps = (obj: any): any => {
    if (obj === null || obj === undefined) return obj;
    if (obj instanceof Date) return obj.toISOString();
    if (obj instanceof Timestamp) return obj.toDate().toISOString();
    if (Array.isArray(obj)) return obj.map(convertTimestamps);
    if (typeof obj === 'object') {
      const converted: any = {};
      for (const key in obj) {
        converted[key] = convertTimestamps(obj[key]);
      }
      return converted;
    }
    return obj;
  };
  
  return {
    exportMetadata: {
      exportedAt,
      userId,
      email,
      requestId
    },
    profile: convertTimestamps(profile),
    businessData: convertTimestamps(businessData),
    tasks: convertTimestamps(tasks),
    documents: convertTimestamps(documents),
    entityForms: {
      drafts: entityDrafts,
      submissions: entitySubmissions
    },
    subscriptions: convertTimestamps(subscriptions),
    auditLogs: convertTimestamps(auditLogs)
  };
}
